import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

interface CommonPageHeaderProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  onRefresh?: () => void;
  refreshButtonText?: string;
  showLastUpdate?: boolean;
  children?: React.ReactNode;
  actionButton?: React.ReactNode;
}

const CommonPageHeader: React.FC<CommonPageHeaderProps> = ({
  title,
  subtitle,
  icon: Icon,
  onRefresh,
  refreshButtonText = 'Frissítés',
  showLastUpdate = true,
  children,
  actionButton
}) => {
  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-3 sm:p-4 lg:p-6 text-white">
      {/* Mobile layout - stacked */}
      <div className="block sm:hidden">
        {/* Title section */}
        <div className="flex items-center space-x-3 mb-3">
          <Icon className="w-5 h-5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold truncate">{title}</h1>
            <p className="text-indigo-100 text-xs truncate">{subtitle}</p>
          </div>
        </div>
        
        {/* Actions section - stacked on very small screens */}
        <div className="space-y-2">
          {/* Timestamp row */}
          {showLastUpdate && (
            <div className="flex justify-start">
              <span className="text-indigo-100 text-xs whitespace-nowrap">
                {format(new Date(), 'HH:mm', { locale: hu })}
              </span>
            </div>
          )}
          
          {/* Buttons row - allow wrapping */}
          <div className="flex flex-wrap items-center gap-2">
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="inline-flex items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-md hover:bg-white/30 transition-colors text-xs whitespace-nowrap"
              >
                <ArrowPathIcon className="w-4 h-4 mr-1" />
                <span className="hidden min-[400px]:inline">{refreshButtonText}</span>
                <span className="min-[400px]:hidden">↻</span>
              </button>
            )}
            {children && (
              <div className="flex flex-wrap items-center gap-2 [&>button]:px-3 [&>button]:py-1.5 [&>button]:text-xs [&>button]:rounded-md [&>button>svg]:w-4 [&>button>svg]:h-4 [&>button>svg]:mr-1 [&>button]:whitespace-nowrap">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tablet layout - compact horizontal */}
      <div className="hidden sm:block md:hidden">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <Icon className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold truncate">{title}</h1>
              <p className="text-indigo-100 text-sm truncate">{subtitle}</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-2 flex-shrink-0">
            {/* Timestamp row */}
            {showLastUpdate && (
              <span className="text-indigo-100 text-xs whitespace-nowrap">
                {format(new Date(), 'HH:mm', { locale: hu })}
              </span>
            )}
            {/* Buttons row */}
            <div className="flex items-center space-x-2 [&>button]:px-3 [&>button]:py-1.5 [&>button]:text-sm [&>button>svg]:w-4 [&>button>svg]:h-4 [&>button>svg]:mr-1 [&>button]:whitespace-nowrap">
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  className="inline-flex items-center px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors text-sm"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  {refreshButtonText}
                </button>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout - full horizontal */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Icon className="w-6 h-6" />
            <div>
              <h1 className="text-xl lg:text-2xl font-bold">{title}</h1>
              <p className="text-indigo-100 text-sm lg:text-base">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {actionButton ? actionButton : (
              <>
                {onRefresh && (
                  <button
                    onClick={onRefresh}
                    className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
                  >
                    <ArrowPathIcon className="w-5 h-5 mr-2" />
                    {refreshButtonText}
                  </button>
                )}
                {showLastUpdate && (
                  <span className="text-indigo-100 text-sm">
                    Utolsó frissítés: {format(new Date(), 'HH:mm:ss', { locale: hu })}
                  </span>
                )}
                {children}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonPageHeader;